import React, { useState, useEffect } from "react";
import Sidebar from "./Sidebar";
import Navbar from "./Navbar";
import { Layout, BackTop, FloatButton } from "antd";
import { ArrowUpOutlined, QuestionCircleOutlined } from "@ant-design/icons";
import styles from "./Layout.module.css";
import "../styles/design-system.css";
import { useKeycloak } from "@react-keycloak/web";
import CustomNotificationContainer from "../components/message/CustomNotificationContainer";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

const { Content, Footer } = Layout;

const AppLayout = ({ children }) => {
    const [collapsed, setCollapsed] = useState(true);
    const [isMobile, setIsMobile] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const { keycloak } = useKeycloak();
    const { t } = useTranslation();

    useEffect(() => {
        const handleResize = () => {
            const mobile = window.innerWidth < 768;
            setIsMobile(mobile);
            if (mobile) {
                setCollapsed(true);
            }
        };
        
        handleResize();
        window.addEventListener("resize", handleResize);
        
        // Simulate loading completion
        const timer = setTimeout(() => setIsLoading(false), 500);
        
        return () => {
            window.removeEventListener("resize", handleResize);
            clearTimeout(timer);
        };
    }, []);

    const sidebarCollapsed = isMobile ? true : collapsed;
    
    const toggleSidebar = () => {
        if (!isMobile) {
            setCollapsed(!collapsed);
        }
    };

    if (isLoading) {
        return (
            <div className={styles.loadingContainer}>
                <div className={styles.loadingSpinner}>
                    <div className={styles.spinner}></div>
                    <p className={styles.loadingText}>{t("loadingApplication")}</p>
                </div>
            </div>
        );
    }

    return (
        <Layout className={`${styles.layout} animate-fade-in`}>
            {/* Enhanced Navbar - positioned at the very top */}
            <div className={styles.navbarWrapper}>
                <Navbar
                    toggleSidebar={toggleSidebar}
                    isToggleDisabled={isMobile}
                    collapsed={sidebarCollapsed}
                    isMobile={isMobile}
                />
            </div>
            
            {/* Sidebar with smooth transitions */}
            {keycloak.authenticated && (
                <div className={`${styles.sidebarWrapper} ${sidebarCollapsed ? styles.sidebarCollapsed : styles.sidebarExpanded}`}>
                    <Sidebar 
                        collapsed={sidebarCollapsed} 
                        onCollapse={toggleSidebar}
                        isMobile={isMobile}
                    />
                </div>
            )}
            
            {/* Main content area */}
            <Layout className={`${styles.mainContent} ${sidebarCollapsed ? styles.collapsed : styles.expanded}`}>
                
                {/* Notification System */}
                <CustomNotificationContainer />
                
                {/* Main Content with smooth transitions */}
                <Content className={`${styles.content} animate-slide-in-right`}>
                    <div className={styles.contentWrapper}>
                        {children}
                    </div>
                </Content>
                
                {/* Enhanced Footer */}
                <Footer className={styles.footer}>
                    <div className={styles.footerContent}>
                        <div className={styles.footerLinks}>
                            <Link to="/privacy-policy" className={styles.footerLink}>
                                {t("privacyLink")}
                            </Link>
                            <span className={styles.footerSeparator}>•</span>
                            <Link to="/impressum" className={styles.footerLink}>
                                {t("impressumLink")}
                            </Link>
                        </div>
                        <div className={styles.footerCopyright}>
                            © 2026 MediSTime {t("allRightsReserved")}
                        </div>
                    </div>
                </Footer>
            </Layout>
            
            {/* Floating Action Buttons */}
            <FloatButton.Group
                trigger="hover"
                type="primary"
                style={{ right: 24, bottom: 24 }}
                icon={<QuestionCircleOutlined />}
            >
                <BackTop>
                    <FloatButton 
                        icon={<ArrowUpOutlined />} 
                        tooltip={t("backToTop")}
                    />
                </BackTop>
            </FloatButton.Group>
            
            {/* Mobile overlay for sidebar */}
            {isMobile && !sidebarCollapsed && (
                <div 
                    className={styles.mobileOverlay}
                    onClick={toggleSidebar}
                    aria-label={t("closeSidebar")}
                />
            )}
        </Layout>
    );
};

export default AppLayout;